import {
	useRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	ForwardRefRenderFunction,
	forwardRef,
	CSSProperties,
	MutableRefObject,
	useReducer,
	useState,
	Dispatch,
	SetStateAction,
} from 'react';
import './SearchFilter.css';

// Components
import Tabs from '../../shared/tab-navigation/tabs/Tabs';
import TabPane from '../../shared/tab-navigation/tab-pane/TabPane';
import { SitesForm, BuildingsForm, Radius } from './components';
import { MobileMainContentTypes } from '../mobile-view-nav';
import DetailsList from '../details-list/DetailsList';
import { useScreenSize } from '../../../context/useScreenSize';

// reducer
import { propertyReducer, ActionTypes, State, SearchTypes } from '../reducer/propertyReducer';

// Esri Imports
import Extent from "@arcgis/core/geometry/Extent";
import Polygon from "@arcgis/core/geometry/Polygon";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import { iForm } from './components/forms/interfaces';


type SearchFilterHandle = {
	onReset: () => void;
	getFiltersSnapshot: () => State;
}

type StateGeometry = Record<string, __esri.Geometry>;
interface StateOption extends Object { value: string; label: string; };
interface PropertyLocation extends Object {
	state: string;
	region: string;
	city: string;
	county: string;
	type: string;
};

interface iProps {
	view: MutableRefObject<__esri.MapView>;
	detailsList?: React.RefObject<React.ElementRef<typeof DetailsList>>;
	mapDiv?: MutableRefObject<null>;
	isSmallScreen: boolean;
	visible: boolean;
	setStateFilterGeometry: Dispatch<SetStateAction<__esri.Polygon | null>>;
	setRadiusFilterGeometry: React.Dispatch<React.SetStateAction<__esri.Polygon | null>>;
	stateOptions: StateOption[];
	stateGeometries: MutableRefObject<StateGeometry>;
	regionOptions: Set<string>;
	extent: Extent;
	displaySearchFilter: boolean;
	onClose: Dispatch<SetStateAction<boolean>>;
	filterByMapExtent: Dispatch<SetStateAction<boolean>>;
	filterByMapExtentValue: boolean;
	setMobileMainContent: Dispatch<SetStateAction<MobileMainContentTypes>>;
	showMap: () => void;
}

const highlightLayerId: string = 'StateFilterHighlight';
const radiusSketchLayerId: string = "RadiusSketch";

// export default function SearchFilter(props: iProps) {
const SearchFilter: ForwardRefRenderFunction<SearchFilterHandle, iProps> = (props, ref) => {
	useImperativeHandle(ref, () => ({ onReset, getFiltersSnapshot }));

	const radius = useRef<React.ElementRef<typeof Radius>>(null);

	const {
		setStateFilterGeometry,
		setRadiusFilterGeometry
	} = props;

	const filtersInitialzed = useRef(false);

	// Holds state of SearchFilter
	//b = buldings s = sites
	// #region Search Reducer
	const initState: State = {
		// displaySearchFilter: props.displaySearchFilter,
		activeSearch: SearchTypes.Both,
		selectedTabIndex: 0,

		//buildings
		bKeywordSearch: '',
		bAvailableSqFtMin: '',
		bAvailableSqFtMax: '',
		bAvailableAcresMin: '',
		bAvailableAcresMax: '',
		bBuildingTypes: new Set<string>(),
		bSpecialtyFeatures: new Set<string>(),
		bRailServed: 'All',
		bMultiTenant: 'All',
		bZoning: new Set<string>(),
		bReviewStatus: new Set<string>(),
		bSaleOrLease: 'All',
		bSortBy: 'Name A-Z',
		bStates: new Set<string>(),
		bRegions: new Set<string>(),
		bCounties: new Set<string>(),
		bCities: new Set<string>(),

		//sites
		sKeywordSearch: '',
		// sAvailableSqFtMin: '',
		// sAvailableSqFtMax: '',
		sAvailableAcresMin: '',
		sAvailableAcresMax: '',
		sSpecialtyFeatures: new Set<string>(),
		sRailServed: 'All',
		sZoning: new Set<string>(),
		sReviewStatus: new Set<string>(),
		sSaleOrLease: 'All',
		sSiteCertified: 'All',
		sStates: new Set<string>(),
		sRegions: new Set<string>(),
		sCounties: new Set<string>(),
		sCities: new Set<string>(),
		sSortBy: 'Name A-Z',

		properties: [],
	};

	const [state, dispatch] = useReducer(propertyReducer, initState);
	const searchState = useRef(state);
	// const [searchState, setSearchState] = useState(state);

	const [initialExtent, setInitialExtent] = useState<Extent>();
	// #endregion

	const [filteredBCities, setFilteredBCities] = useState<string[]>([]);
	const [filteredSCities, setFilteredSCities] = useState<string[]>([]);
	const [filteredBCounties, setFilteredBCounties] = useState<string[]>([]);
	const [filteredSCounties, setFilteredSCounties] = useState<string[]>([]);
	const [bCityOpts, setBCityOpts] = useState<string[]>([]);
	const [sCityOpts, setSCityOpts] = useState<string[]>([]);
	const [bCountyOpts, setBCountyOpts] = useState<string[]>([]);
	const [sCountyOpts, setSCountyOpts] = useState<string[]>([]);
	const [propertyLayerTableNames, setPropertyLayerTableNames] = useState<string[]>([]);
	const [propertyLayerFields , setPropertyLayerFields] = useState<Record<string, string>>({});

	const [propLocData, setPropLocData] = useState<PropertyLocation[]>([]);

	//Set to properties when available
	const transloadLayer: MutableRefObject<__esri.FeatureLayer | undefined> = useRef();
	const propertiesLayer: MutableRefObject<__esri.FeatureLayer | undefined> = useRef();

	const highlightLayer: MutableRefObject<__esri.GraphicsLayer> = useRef(
		new GraphicsLayer({
			id: highlightLayerId,
			listMode: 'hide'
		})
	);

	const radiusSketchLayer = useRef<__esri.GraphicsLayer>(
		new GraphicsLayer({
			id: radiusSketchLayerId,
			listMode: 'hide'
		})
	);

	// TODO - MAP OVERRIDE
	const initFilters = () => {
		props.view.current.when(() => {
			setInitialExtent(props.view.current.extent)
			if (!filtersInitialzed.current) {
				filtersInitialzed.current = true;

				if (!props.view.current.map.findLayerById(radiusSketchLayerId)) {
					props.view.current.map.add(radiusSketchLayer.current);
				}

				propertiesLayer.current = props.view.current.map.layers
					.find(table => table.title === 'Properties') as __esri.FeatureLayer;


				//#UTIL - print current field names, aliases - now using for keyword search terms
				propertiesLayer.current?.when(() => {
					if (propertiesLayer.current && propertiesLayer.current.fields) {
					  const fieldMap: Record<string, string> = {};
					  propertiesLayer.current.fields.forEach(e => {
						fieldMap[e.name] = e.type;
					  });
					  setPropertyLayerFields(fieldMap);
					} else {
					  console.error('Layer fields are null or not loaded');
					}
				  }).catch((err) => {
					console.error('Error loading fields:', err);
				  });
				  
				if (!props.view.current.map.findLayerById(highlightLayerId)) {
					props.view.current.map.add(highlightLayer.current);
				}
				
				let propertyLocation: PropertyLocation[] = [];
				propertiesLayer.current?.queryFeatures({
					where: "1=1",
					outFields: ['*']
					// outFields: ["site_state","site_region","sitebuilding_city","site_county"]
				}).then(res => {

					dispatch({ type: ActionTypes.SetProperties, payload: res.features });

					res.features.forEach(feat => {
						propertyLocation.push({
							"state": feat.attributes.state?.trim(),
							"region": feat.attributes.site_region?.trim(),
							"city": feat.attributes.sitebuilding_city?.trim(),
							"county": feat.attributes.site_county?.trim(),
							"type": feat.attributes?.is_this_a_site_or_building
						})
					})
					setPropLocData(propertyLocation);

				}).catch(console.error);
			}
		}).catch(console.error);
	}
	useEffect(initFilters, [props.view])

	const updateCityCountyOpts = () => {
		let bCityOptsTemp: Set<string> = new Set<string>();
		let bCountyOptsTemp: Set<string> = new Set<string>();
		let sCityOptsTemp: Set<string> = new Set<string>();
		let sCountyOptsTemp: Set<string> = new Set<string>();

		//states
		if (state.bStates.size > 0 && (state.activeSearch === 'Buildings' || state.activeSearch === 'Both')) {
			const statesCode = Array.from(state.bStates).map(field => props.stateOptions?.find(e => e.value === field)?.value) //#Performance
			propLocData.forEach(e => {
				if ((statesCode.includes(e.state)) && ['Building', 'Both'].includes(e.type)) {
					bCityOptsTemp.add(e.city)
					bCountyOptsTemp.add(e.county)
				}
			})
		}
		if (state.sStates.size > 0 && state.activeSearch === 'Sites') {
			const statesCode = Array.from(state.sStates).map(field => props.stateOptions?.find(e => e.value === field)?.value) //#Performance
			propLocData.forEach(e => {
				if (statesCode.includes(e.state) && ['Both', 'Site'].includes(e.type)) {
					sCityOptsTemp.add(e.city)
					sCountyOptsTemp.add(e.county)
				}
			})
		}

		//regions
		if (state.bRegions.size > 0) {
			propLocData.forEach(e => {
				if (state.bRegions.has(e.region?.toUpperCase()) && ['Both', 'Building'].includes(e.type)) {
					bCityOptsTemp.add(e.city)
					bCountyOptsTemp.add(e.county)
				}
			})
		}
		if (state.sRegions.size > 0) {
			propLocData.forEach(e => {
				if (state.sRegions.has(e.region?.toUpperCase()) && ['Both', 'Site'].includes(e.type)) {
					sCityOptsTemp.add(e.city)
					sCountyOptsTemp.add(e.county)
				}
			})
		}

		setFilteredBCities(Array.from(bCityOptsTemp).sort())
		setFilteredSCities(Array.from(sCityOptsTemp).sort())
		setFilteredBCounties(Array.from(bCountyOptsTemp).sort())
		setFilteredSCounties(Array.from(sCountyOptsTemp).sort())

		setBCityOpts(Array.from(bCityOptsTemp).sort())
		setSCityOpts(Array.from(sCityOptsTemp).sort())
		setBCountyOpts(Array.from(bCountyOptsTemp).sort())
		setSCountyOpts(Array.from(sCountyOptsTemp).sort())

		if (state.bStates.size === 0 && state.sStates.size === 0 && state.bRegions.size === 0 && state.sRegions.size === 0) {
			dispatch({ type: ActionTypes.SetBCounties, payload: new Set() })
			dispatch({ type: ActionTypes.SetSCounties, payload: new Set() })
			dispatch({ type: ActionTypes.SetBCities, payload: new Set() })
			dispatch({ type: ActionTypes.SetSCities, payload: new Set() })

			setBCityOpts([])
			setSCityOpts([])
			setBCountyOpts([])
			setSCountyOpts([])

			//disabled message

		}
	}
	useEffect(updateCityCountyOpts,
		[
			state.bStates,
			state.sStates,
			state.bRegions,
			state.sRegions,
			propLocData,
			props.stateOptions,
			state.activeSearch
		]);

	const onCityCountyChange = () => {
		//filters city/county based on which was selected first
		const filteredBLocs = propLocData.filter(e => filteredBCities.includes(e.city) && filteredBCounties.includes(e.county))
		const filteredSLocs = propLocData.filter(e => filteredSCities.includes(e.city) && filteredSCounties.includes(e.county))

		let bCityOptsTemp: Set<string> = new Set<string>();
		let bCountyOptsTemp: Set<string> = new Set<string>();
		let sCityOptsTemp: Set<string> = new Set<string>();
		let sCountyOptsTemp: Set<string> = new Set<string>();

		//bldgs
		if (state.bCounties.size > 0 && state.bCities.size === 0) {
			filteredBLocs.forEach(e => {
				if (state.bCounties.has(e.county)) {
					bCityOptsTemp.add(e.city)
				}
			})
			setBCityOpts(Array.from(bCityOptsTemp).sort())
			setBCountyOpts(filteredBCounties)
		}
		else if (state.bCounties.size === 0 && state.bCities.size > 0) {
			filteredBLocs.forEach(e => {
				if (state.bCities.has(e.city)) {
					bCountyOptsTemp.add(e.county)
				}
			})
			setBCountyOpts(Array.from(bCountyOptsTemp).sort())
			setBCityOpts(filteredBCities)

		}
		else if (state.bCounties.size === 0 && state.bCities.size === 0) {
			setBCityOpts(filteredBCities)
			setBCountyOpts(filteredBCounties)
		}

		//sites
		if (state.sCounties.size > 0 && state.sCities.size === 0) {
			filteredSLocs.forEach(e => {
				if (state.sCounties.has(e.county)) {
					sCityOptsTemp.add(e.city)
				}
			})
			setSCityOpts(Array.from(sCityOptsTemp).sort())
			setSCountyOpts(filteredSCounties)

		}
		else if (state.sCounties.size === 0 && state.sCities.size > 0) {
			filteredSLocs.forEach(e => {
				if (state.sCities.has(e.city)) {
					sCountyOptsTemp.add(e.county)
				}
			})
			setSCountyOpts(Array.from(sCountyOptsTemp).sort())
			setSCityOpts(filteredSCities)
		}
		else if (state.sCounties.size === 0 && state.sCities.size === 0) {
			setSCityOpts(filteredSCities)
			setSCountyOpts(filteredSCounties)
		}

	}
	useEffect(onCityCountyChange,
		[
			state.bCounties,
			state.bCities,
			state.sCounties,
			state.sCities,
			filteredBCities,
			filteredBCounties,
			filteredSCounties,
			filteredSCities,
			propLocData,
			setSCityOpts,
		]);

	const generateDefExpFromMultiSelect = (tableName: string, property: Set<string>) => {
		let termsFromSelection: string[] = [];
		property.forEach(e => {
			termsFromSelection.push(`${tableName} like '%${e.replaceAll(' ', '_')}%'`);
		});
		const expression = termsFromSelection.map(term => `(${term})`).join(" OR ");
		return expression;
	}

	const generateBuildingExpressions = (terms: Array<string>) => {
		if (state.activeSearch === SearchTypes.Building){
			const tableName = 'is_this_a_site_or_building';
			const selected = ['Building', 'Both'].map(field => `'${field}'`).join(',');
			const expression = `(${tableName} IN (${selected}) OR ${tableName} is null)`;
			terms.push(expression)
		}

		if (state.bKeywordSearch.length > 0) {
			const expressions: string[] = [];
			const keyword = state.bKeywordSearch.toLowerCase();
		
			for (const [tableName, tableType] of Object.entries(propertyLayerFields)) {
				if (tableType === 'string') {
					if (tableName === 'state') {
						const matchedState = props.stateOptions.find(option => option.label.toLowerCase() === keyword);
						const stateValue = matchedState ? matchedState.value.toLowerCase() : keyword;
						expressions.push(`LOWER(${tableName}) LIKE '%${stateValue}%'`);
					} else {
						expressions.push(`LOWER(${tableName}) LIKE '%${keyword}%'`);
					}
				} else if (tableType === 'double') {
					const numericValue = Number(state.bKeywordSearch);
					if (!isNaN(numericValue)) {
						expressions.push(`${tableName} = ${numericValue}`);
					}
				}
			}
		
			const expression = expressions.join(' OR ');
		
			if (expression) {
				terms.push(expression);
			}
		}
		if (state.bBuildingTypes.size > 0) {
			const tableName = 'building_type';

			//OLD PATTERN FOR REFERENCE---
			// let termsFromSelection: string[] = [];
			// state.bBuildingTypes.forEach(e => {
			//   termsFromSelection.push(`${tableName} like '%${e.replace(' ','_')}%'`);
			// });
			// const expression = termsFromSelection.map(term => `(${term})`).join(" OR ");
			//---
			const expression = generateDefExpFromMultiSelect(tableName, state.bBuildingTypes);
			terms.push(expression)
		}
		if (state.bSpecialtyFeatures.size > 0) {
			const tableName = 'building_specialty_features';
			const expression = generateDefExpFromMultiSelect(tableName, state.bSpecialtyFeatures);
			terms.push(expression)
		}
		if (state.bZoning.size > 0) {
			const tableName = 'building_zoning';
			const expression = generateDefExpFromMultiSelect(tableName, state.bZoning);
			terms.push(expression)
		}
		// if (state.bReviewStatus.size > 0) {
		//   const tableName = 'review_status';
		//   const expression = generateDefExpFromMultiSelect(tableName, state.bReviewStatus);
		//   terms.push(expression)
		// }

		if (state.bStates.size > 0) {
			const tableName = 'state';
			const stateCode = Array.from(state.bStates).map(field => `'${props.stateOptions?.find(e => e.value === field)?.value}'`).join(','); //#Performance - if this is slow, could refactor: bStates would carry 'value' and 'label' instead of just 'value'
			const expression = `${tableName} IN (${stateCode})`;
			terms.push(expression)
		}
		if (state.bRegions.size > 0) {
			const tableName = 'site_region';
			const selected = Array.from(state.bRegions).map(field => `'${field}'`).join(',');
			const expression = `${tableName} IN (${selected})`;
			terms.push(expression)
		}
		if (state.bCities.size > 0) {
			const tableName = 'sitebuilding_city';
			const selected = Array.from(state.bCities).map(field => `'${field}'`).join(',');
			const expression = `${tableName} IN (${selected})`;
			terms.push(expression)
		}
		if (state.bCounties.size > 0) {
			const tableName = 'site_county';
			const selected = Array.from(state.bCounties).map(field => `'${field}'`).join(',');
			const expression = `${tableName} IN (${selected})`;
			terms.push(expression)
		}
		if (state.bAvailableAcresMin.length > 0) {
			const tableName = 'site_area_acres';
			const selected = state.bAvailableAcresMin as String;
			const expression = `${tableName} >= ${selected}`;
			terms.push(expression)
		}
		if (state.bAvailableAcresMax.length > 0) {
			const tableName = 'site_area_acres';
			const selected = state.bAvailableAcresMax;
			const expression = `${tableName} <= ${selected}`;
			terms.push(expression)
		}
		if (state.bAvailableSqFtMin.length > 0) {
			const tableName = 'building_area_sq_ft';
			const selected = state.bAvailableSqFtMin;
			const expression = `${tableName} >= ${selected}`;
			terms.push(expression)
		}
		if (state.bAvailableSqFtMax.length > 0) {
			const tableName = 'building_area_sq_ft';
			const selected = state.bAvailableSqFtMax;
			const expression = `${tableName} <= ${selected}`;
			terms.push(expression)
		}
		if (state.bRailServed !== 'All') {
			const tableName = 'building_rail_served';
			const selected = state.bRailServed === 'Maybe' ? 'Potential' : state.bRailServed;
			const expression = `${tableName} = '${selected}'`;
			terms.push(expression)
		}
		if (state.bMultiTenant !== 'All') {
			const tableName = 'building_tenancy';
			const selected = state.bMultiTenant;
			const expression = `${tableName} = '${selected}'`;
			terms.push(expression)
		}
		if (state.bSaleOrLease !== 'All') {
			const tableName = 'building_for_sale_or_lease';
			const selected = [`For ${state.bSaleOrLease}`, 'Both'].map(field => `'${field}'`).join(',');
			const expression = `${tableName} IN (${selected})`;
			terms.push(expression)
		}

	}

	const generateSiteExpressions = (terms: Array<string>) => {
		const tableName = 'is_this_a_site_or_building';
		const selected = ['Site', 'Both'].map(field => `'${field}'`).join(',');
		const expression = `(${tableName} IN (${selected}) OR ${tableName} is null)`;
		terms.push(expression)

		if (state.sKeywordSearch.length > 0) {
			const expressions: string[] = [];
			const keyword = state.sKeywordSearch.toLowerCase();
		
			for (const [tableName, tableType] of Object.entries(propertyLayerFields)) {
				if (tableType === 'string') {
					if (tableName === 'state') {
						const matchedState = props.stateOptions.find(option => option.label.toLowerCase() === keyword);
						const stateValue = matchedState ? matchedState.value.toLowerCase() : keyword;
						expressions.push(`LOWER(${tableName}) LIKE '%${stateValue}%'`);
					} else {
						expressions.push(`LOWER(${tableName}) LIKE '%${keyword}%'`);
					}
				} else if (tableType === 'double') {
					const numericValue = Number(state.sKeywordSearch);
					if (!isNaN(numericValue)) {
						expressions.push(`${tableName} = ${numericValue}`);
					}
				}
			}
		
			const expression = expressions.join(' OR ');
		
			if (expression) {
				terms.push(expression);
			}
		}
		if (state.sSpecialtyFeatures.size > 0) {
			const tableName = 'site_specialty_features';
			const expression = generateDefExpFromMultiSelect(tableName, state.sSpecialtyFeatures);
			terms.push(expression)
		}
		if (state.sZoning.size > 0) {
			const tableName = 'site_zoning';
			const expression = generateDefExpFromMultiSelect(tableName, state.sZoning);
			terms.push(expression)
		}
		// if (state.sReviewStatus.size > 0) {
		//   const tableName = 'review_status';
		//   const expression = generateDefExpFromMultiSelect(tableName, state.sReviewStatus);
		//   terms.push(expression)
		// }
		if (state.sSiteCertified !== 'All') { //#NOTE: this field is linked to 'Site Certified' on both search tabs
			const tableName = 'site_certified';
			const expression = `${tableName} = '${state.sSiteCertified}'`;
			terms.push(expression)
		}
		if (state.sStates.size > 0) {
			const tableName = 'state';
			const stateCode = Array.from(state.sStates).map(field => `'${props.stateOptions?.find(e => e.value === field)?.value}'`).join(','); //#Performance - if this is slow, could refactor: bStates would carry 'value' and 'label' instead of just 'value'
			const expression = `${tableName} IN (${stateCode})`;
			terms.push(expression)
		}
		if (state.sRegions.size > 0) {
			const tableName = 'site_region';
			const selected = Array.from(state.sRegions).map(field => `'${field}'`).join(',');
			const expression = `${tableName} IN (${selected})`;
			terms.push(expression)
		}
		if (state.sCities.size > 0) {
			const tableName = 'sitebuilding_city';
			const selected = Array.from(state.sCities).map(field => `'${field}'`).join(',');
			const expression = `${tableName} IN (${selected})`;
			terms.push(expression)
		}
		if (state.sCounties.size > 0) {
			const tableName = 'site_county';
			const selected = Array.from(state.sCounties).map(field => `'${field}'`).join(',');
			const expression = `${tableName} IN (${selected})`;
			terms.push(expression)
		}

		//#TODO: need new vars for site acres?
		// ^converting site sqt to site acres

		if (state.sAvailableAcresMin.length > 0) {
			const tableName = 'site_area_acres';
			const selected = state.sAvailableAcresMin;
			const expression = `${tableName} >= ${selected}`;
			terms.push(expression)
		}
		if (state.sAvailableAcresMax.length > 0) {
			const tableName = 'site_area_acres';
			const selected = state.sAvailableAcresMax;
			const expression = `${tableName} <= ${selected}`;
			terms.push(expression)
		}
		if (state.sRailServed !== 'All') {
			const tableName = 'site_rail_served';
			const selected = state.sRailServed === 'Maybe' ? 'Potential' : state.sRailServed;
			const expression = `${tableName} = '${selected}'`;
			terms.push(expression)
		}
		if (state.sSaleOrLease !== 'All') {
			const tableName = 'site_for_sale_or_lease';
			const selected = [`For ${state.sSaleOrLease}`, 'Both'].map(field => `'${field}'`).join(',');
			const expression = `${tableName} IN (${selected})`;
			terms.push(expression)
		}
	}

	const onSearch = async () => {

		const radiusResult = await radius.current?.searchCallback();
		dispatch({ type: ActionTypes.SetRadiusCenterPoint, payload: radiusResult });

		// setSearchState(state);
		state.radiusCenterPoint = radiusResult;
		searchState.current = state;


		//#TODO
		// When all selection options are available from data..
		// - may need to replace spaces with underscores, other adjustments on search terms
		if (propertiesLayer.current) {
			// Create expressions for queries
			const terms: Array<string> = [];
			if (state.activeSearch === SearchTypes.Building || state.activeSearch === SearchTypes.Both) { generateBuildingExpressions(terms); }
			else { generateSiteExpressions(terms); }

			const definitionExpression = terms.map(term => `(${term})`).join(" AND ");
		
			propertiesLayer.current.definitionExpression = definitionExpression;

			props.detailsList?.current?.updateDetailsListCallback()
		}

		if (props.isSmallScreen) {
			props.setMobileMainContent(MobileMainContentTypes.DETAILS)
		}
	}

	const onReset = () => {
		dispatch({ type: ActionTypes.SetReset, payload: initState });
		radius.current?.clear();
		searchState.current = initState;

		props.view.current.goTo(initialExtent);
		setRadiusFilterGeometry(null);

		if (propertiesLayer.current) {
			propertiesLayer.current.definitionExpression = '';
		}
	}

	const filterByMapExtentHandler = (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		const value = evt.currentTarget.name;
		value === 'Yes' ? props.filterByMapExtent(true) : props.filterByMapExtent(false);
	}

	const showUnsavedChanges = () => {
		//narrowing down attributes to compare with last search
		const stateAttrs = Object
			.keys(state)
			.filter(e => !['displaySearchFilter', 'activeSearch', 'selectedTabIndex', 'properties'].includes(e))

		const state1 = Object
			.entries(state)
			.filter(([k, v]) => !['displaySearchFilter', 'activeSearch', 'selectedTabIndex', 'properties'].includes(k))

		const searchState1 = Object
			.entries(searchState.current)
			.filter(([k, v]) => !['displaySearchFilter', 'activeSearch', 'selectedTabIndex', 'properties'].includes(k))

		for (let i = 0; i < stateAttrs.length; i++) {
			let a = state1[i]?.[1];
			let b = searchState1[i]?.[1];

			if (a !== b) {
				//handling set comparison
				if (a instanceof Set && b instanceof Set) {
					if (a.size !== b.size) {
						return true
					}
					if (!Array.from(a).every((x) => b.has(x)) && !Array.from(b).every((x) => a.has(x))) {
						return true
					}
				}
				else {
					return true
				}
			}
		}

		return false

	}

	const buildStateFilterGeometry = () => {
		if (!transloadLayer.current) {
			return;
		}

		// if (action == 'reset') {
		//   setStateFilterGeometry(null);
		// }

		if (state.bStates.size > 0) {
			var geometries = Array.from(state.bStates).map(state => props.stateGeometries.current[state]);
			if (geometries.length > 0) {

				var aggregatedPolygon = new Polygon({
					hasM: false,
					hasZ: false,
					spatialReference: geometries.at(0)?.spatialReference
				});

				geometries.forEach(geometry => {
					if (geometry && geometry.type === 'polygon') {
						const polygon = geometry as __esri.Polygon;
						polygon.rings.forEach(ring => {
							aggregatedPolygon.addRing(ring);
						});
					}
				});

				// push filter geom up to parent
				setStateFilterGeometry(aggregatedPolygon);
			}
		} else {
			setStateFilterGeometry(null);
		}
	}
	useEffect(buildStateFilterGeometry, [state.bStates, setStateFilterGeometry, props.stateGeometries]);

	const getFiltersSnapshot = useCallback(() => state, [state]);

	// #region Mobile Logic and CSS
	let displaySearch: CSSProperties = {};
	let stateOptionsStyle: CSSProperties = {};
	let inputStyle: CSSProperties = {};
	const flexDirection = props.isSmallScreen ? 'flex-column' : 'flex-row';
	const { isSquishedScreen: squished } = useScreenSize();
	if (props.isSmallScreen) {
		stateOptionsStyle = {
			// height: '100vh',
			position: 'inherit',
			boxShadow: 'none',
			width: '100%',
			maxWidth: '100%',
			height: 'calc(100% - 0.25px)'
		};

		inputStyle = {
			width: '100%',
			maxWidth: '100%'
		};
	}
	else if (squished) {
		stateOptionsStyle = {
			top: "96px",
			left: "0",
			boxShadow: 'none',
			width: 'calc(100% - 350px)',
			maxWidth: '100%',
			height: 'calc(100% - 96px)'
		};
	}

	if (!props.visible && props.isSmallScreen) {
		displaySearch.display = 'none';
	} else if (props.visible && props.isSmallScreen) {
		displaySearch = {
			overflowY: 'auto',
			overflowX: 'hidden',
			height: '100%',
			width: '100%'
		};
	} else {
		displaySearch = {};
	}
	//#endregion

	const formProps = {
		ref:radius,
		view:props.view,
		radiusSketchLayer: radiusSketchLayer,
		setRadiusFilterGeometry: setRadiusFilterGeometry,
		state: state,
		dispatch: dispatch,
		flexDirection: flexDirection,
		inputStyle: inputStyle,
		stateOptions: props.stateOptions,
		regionOptions: props.regionOptions,
		filterByMapExtentHandler: filterByMapExtentHandler,
		filterByMapExtentValue: props.filterByMapExtentValue,
	} as iForm;

	return (
		<div className="property-search-filter" style={displaySearch}>
			{(props.displaySearchFilter || (props.isSmallScreen && props.visible)) &&
				<div className='property-search-filter-container' style={stateOptionsStyle}>
					<Tabs
						groupLabel='Search Properties'
						groupLabelPosition='left'
						onChange={(evt) => dispatch({ type: ActionTypes.SetActiveSearch, payload: evt })}
						selectedIndex={state.selectedTabIndex}
					>
						<TabPane title='Both' className='filter-container'>
							<BuildingsForm
								{...formProps}
								bCityOpts={bCityOpts}
								bCountyOpts={bCountyOpts}

						/>
						</TabPane>
						<TabPane title='Buildings' className='filter-container'>
							<BuildingsForm
								{...formProps}
								bCityOpts={bCityOpts}
								bCountyOpts={bCountyOpts}

							/>
						</TabPane>
						<TabPane title='Sites' className='filter-container'>
							<SitesForm
								{...formProps}
								sCityOpts={sCityOpts}
								sCountyOpts={sCountyOpts}
							/>
						</TabPane>
					</Tabs>

					<div className="button-actions">
						<button className='bg-orange no-border icon-btn' onClick={onReset}>
							<span className="esri-icon-undo"></span>
							<span>Reset</span>
						</button>
						{/* #TODO: not showing message on small screen for now, breaks height. */}
						{showUnsavedChanges() && !props.isSmallScreen &&
							<span className='warn'>Click Search to apply changes</span>
						}
						{props.isSmallScreen
							? <button onClick={() => props.setMobileMainContent(MobileMainContentTypes.MAP)}>Close</button>
							: <button onClick={() => props.onClose(false)}>Close</button>
						}
						<button className='bg-orange no-border icon-btn' onClick={onSearch}>
							<span>Search</span>
							<span className="esri-icon-reply"></span>
						</button>
					</div>
				</div>
			}
		</div>
	)
}

export default forwardRef(SearchFilter);